import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { ArrowLeft, Plus, Trash2, Send, Save, Edit3, Upload, FileText, X } from 'lucide-react';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';

const BatchCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  
  // Batch state
  const [batchReference, setBatchReference] = useState('');
  const [description, setDescription] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [batchId, setBatchId] = useState(null);
  const [batchStatus, setBatchStatus] = useState('DRAFT');
  
  // Payments state
  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(isEditing);

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    confirmText: '',
    confirmStyle: 'btn-primary',
    onConfirm: null,
  });

  // Fetch approved suppliers and cost centres on mount
  useEffect(() => {
    fetchSuppliers();
    fetchCostCentres();
    
    if (isEditing) {
      fetchBatch();
    } else {
      setBatchReference(generateBatchReference());
    }
  }, [id]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get('/api/suppliers/?approval_status=APPROVED');
      setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
    }
  };

  const fetchCostCentres = async () => {
    try {
      const response = await api.get('/api/cost-centres/');
      setCostCentres(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to load cost centres:', err);
    }
  };

  const fetchBatch = async () => {
    try {
      setInitialLoading(true);
      const response = await api.get(`/api/batches/${id}/`);
      const batch = response.data;
      
      // Check if batch is still editable
      if (batch.status !== 'DRAFT') {
        setError('This batch can no longer be edited');
        setTimeout(() => navigate(`/batches/${id}`), 2000);
        return;
      }
      
      // Populate batch fields
      setBatchId(batch.id);
      setBatchReference(batch.batch_reference);
      setDescription(batch.description || '');
      setPaymentDate(batch.payment_date || '');
      setBatchStatus(batch.status);
      
      // Populate payments
      const loadedPayments = batch.payments.map(p => ({
        id: p.id,
        tempId: p.id,
        supplier: p.supplier,
        cost_centre: p.cost_centre || '',
        amount: p.amount,
        reference: p.reference,
        invoice_number: p.invoice_number || '',
        currency: p.currency,
        invoice: null,
        invoiceFileName: p.invoice ? p.invoice.split('/').pop() : '',
        invoiceUrl: p.invoice || '',
        saved: true,
      }));
      setPayments(loadedPayments);
      
    } catch (err) {
      setError('Failed to load batch');
      console.error(err);
    } finally {
      setInitialLoading(false);
    }
  };

  const generateBatchReference = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const time = String(date.getHours()).padStart(2, '0') + String(date.getMinutes()).padStart(2, '0');
    const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return `BATCH-${year}${month}${day}-${time}${random}`;
  };

  // Create the batch
  const createBatch = async () => {
    if (!batchReference.trim()) {
      setError('Batch reference is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await api.post('/api/batches/', {
        batch_reference: batchReference,
        description,
        payment_date: paymentDate || null,
      });
      
      setBatchId(response.data.id);
      setError(null);
    } catch (err) {
      console.log('Batch save error:', err.response?.data);
      const errorData = err.response?.data;
      if (errorData) {
        const firstError = Object.entries(errorData)[0];
        setError(`${firstError[0]}: ${firstError[1]}`);
      } else {
        setError('Failed to create batch');
      }
    } finally {
      setSaving(false);
    }
  };

  // Update batch details
  const updateBatch = async () => {
    setSaving(true);
    setError(null);

    try {
      await api.patch(`/api/batches/${batchId}/`, {
        description,
        payment_date: paymentDate || null,
      });
      setError(null);
    } catch (err) {
      setError('Failed to update batch');
    } finally {
      setSaving(false);
    }
  };

  // Add empty payment row
  const addPaymentRow = () => {
    setPayments([
      ...payments,
      {
        id: null,
        tempId: Date.now(),
        supplier: '',
        cost_centre: '',
        amount: '',
        reference: '',
        invoice_number: '',
        currency: '',
        invoice: null,
        invoiceFileName: '',
        saved: false,
      },
    ]);
  };

  // Update payment row
  const updatePayment = (index, field, value) => {
    const updated = [...payments];
    updated[index][field] = value;
    updated[index].saved = false;

    // Auto-populate currency when supplier is selected
    if (field === 'supplier' && value) {
      const supplier = suppliers.find((s) => s.id === parseInt(value));
      if (supplier) {
        updated[index].currency = supplier.currency;
      }
    }

    setPayments(updated);
  };

  // Handle invoice file selection
  const handleInvoiceChange = (index, file) => {
    setPayments(prev => prev.map((p, i) => 
      i === index ? { 
        ...p, 
        invoice: file, 
        invoiceFileName: file ? file.name : '',
        saved: false 
      } : p
    ));
  };

  // Save individual payment
  const savePayment = async (index) => {
    const payment = payments[index];

    if (!payment.supplier || !payment.amount || !payment.reference) {
      setError('Supplier, amount, and reference are required');
      return;
    }

    setError(null);
    
    // Set saving state
    setPayments(prev => prev.map((p, i) => 
      i === index ? { ...p, saving: true } : p
    ));

    try {
      let response;
      
      // Use FormData to handle file upload
      const formData = new FormData();
      formData.append('supplier', parseInt(payment.supplier));
      formData.append('amount', payment.amount);
      formData.append('reference', payment.reference);
      formData.append('invoice_number', payment.invoice_number || '');
      
      // Add cost centre if selected
      if (payment.cost_centre) {
        formData.append('cost_centre', parseInt(payment.cost_centre));
      }
      
      if (payment.invoice) {
        formData.append('invoice', payment.invoice);
      }
      
      if (payment.id) {
        response = await api.patch(`/api/payments/${payment.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        formData.append('batch', batchId);
        response = await api.post('/api/payments/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // Update with saved data
      setPayments(prev => prev.map((p, i) => 
        i === index ? {
          ...p,
          id: response.data.id,
          currency: response.data.currency,
          cost_centre: response.data.cost_centre || '',
          invoiceFileName: response.data.invoice ? response.data.invoice.split('/').pop() : '',
          invoiceUrl: response.data.invoice || '',
          saved: true,
          saving: false,
        } : p
      ));
      
    } catch (err) {
      console.log('Payment save error:', err.response?.data);
      
      // Reset saving state
      setPayments(prev => prev.map((p, i) => 
        i === index ? { ...p, saving: false } : p
      ));
      
      const errorData = err.response?.data;
      if (errorData) {
        if (typeof errorData === 'string') {
          setError(errorData);
        } else if (errorData.detail) {
          setError(errorData.detail);
        } else {
          const firstError = Object.entries(errorData)[0];
          setError(`${firstError[0]}: ${firstError[1]}`);
        }
      } else {
        setError('Failed to save payment');
      }
    }
  };

  // Enable editing a saved payment
  const editPayment = (index) => {
    setPayments(prev => prev.map((p, i) => 
      i === index ? { ...p, saved: false } : p
    ));
  };

  // Delete payment
  const deletePayment = async (index) => {
    const payment = payments[index];

    setConfirmModal({
      show: true,
      title: 'Delete Payment',
      message: payment.supplier
        ? `Are you sure you want to delete the payment to ${
            suppliers.find(s => s.id === parseInt(payment.supplier))?.supplier_name || 'this supplier'
          }${payment.amount ? ` for ${payment.currency || 'ZAR'} ${parseFloat(payment.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : ''}?`
        : 'Are you sure you want to delete this payment?',
      confirmText: 'Delete',
      confirmStyle: 'btn-danger',
      onConfirm: async () => {
        if (payment.id) {
          try {
            await api.delete(`/api/payments/${payment.id}/`);
          } catch (err) {
            setError('Failed to delete payment');
            setConfirmModal(prev => ({ ...prev, show: false }));
            return;
          }
        }
        setPayments(payments.filter((_, i) => i !== index));
        setConfirmModal(prev => ({ ...prev, show: false }));
      },
    });
  };

  // Submit batch for approval
  const submitBatch = async () => {
    if (!paymentDate) {
      setError('Payment date is required before submission');
      return;
    }

    const unsavedPayments = payments.filter((p) => !p.saved && (p.supplier || p.amount || p.reference));
    if (unsavedPayments.length > 0) {
      setError('Please save all payments before submitting');
      return;
    }

    const savedPayments = payments.filter((p) => p.saved);
    if (savedPayments.length === 0) {
      setError('Add at least one payment before submitting');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Update batch with payment date first
      await api.patch(`/api/batches/${batchId}/`, {
        payment_date: paymentDate,
      });

      // Submit for approval
      await api.post(`/api/batches/${batchId}/submit/`);
      navigate('/batches');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit batch');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals grouped by currency
  const currencyTotals = payments
    .filter((p) => p.amount && p.currency)
    .reduce((acc, p) => {
      const currency = p.currency || 'ZAR';
      acc[currency] = (acc[currency] || 0) + parseFloat(p.amount || 0);
      return acc;
    }, {});

  const savedPaymentsCount = payments.filter((p) => p.saved).length;

  // react-select custom styles
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px',
      minWidth: '220px',
      borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
      '&:hover': {
        borderColor: '#9ca3af',
      },
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
          ? '#eff6ff' 
          : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9ca3af',
    }),
  };

  // Build options for react-select
  const supplierOptions = suppliers.map(s => ({
    value: s.id,
    label: `${s.supplier_name} (${s.service_category})`,
  }));

  const costCentreOptions = costCentres.map(cc => ({
    value: cc.id,
    label: `${cc.code} - ${cc.name}`,
  }));

  // Helper to find selected option
  const getSupplierOption = (supplierId) => {
    if (!supplierId) return null;
    return supplierOptions.find(opt => opt.value === parseInt(supplierId)) || null;
  };

  const getCostCentreOption = (costCentreId) => {
    if (!costCentreId) return null;
    return costCentreOptions.find(opt => opt.value === parseInt(costCentreId)) || null;
  };

  if (initialLoading) {
    return <div className="loading">Loading batch...</div>;
  }

  return (
    <div className="batch-create-container">
      {/* Confirmation Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmStyle={confirmModal.confirmStyle}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, show: false }))}
      />
      {/* Header */}
      <div className="batch-create-header">
        <button className="btn btn-secondary" onClick={() => navigate('/batches')}>
          <ArrowLeft size={18} />
          Back to Batches
        </button>
        <h1>{isEditing ? 'Edit Batch' : 'Create New Batch'}</h1>
      </div>

      {/* Error message */}
      {error && <div className="error-banner">{error}</div>}

      {/* Batch Details Card */}
      <div className="card">
        <h2>Batch Details</h2>
        <div className="form-grid">
          <div className="form-group">
            <label>Batch Reference *</label>
            <input
              type="text"
              value={batchReference}
              onChange={(e) => setBatchReference(e.target.value)}
              placeholder="BATCH-202601-001"
              disabled={!!batchId}
            />
          </div>
          <div className="form-group">
            <label>Payment Date {batchId ? '*' : '(can set later)'}</label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </div>
          <div className="form-group full-width">
            <label>Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="January supplier payments"
            />
          </div>
        </div>

        {/* Create Batch Button - Only show if batch not yet created */}
        {!batchId && (
          <div className="card-actions">
            <button className="btn btn-primary" onClick={createBatch} disabled={saving}>
              <Save size={18} />
              {saving ? 'Creating...' : 'Create Batch'}
            </button>
          </div>
        )}

        {/* Update Button - Only show if batch exists */}
        {batchId && (
          <div className="card-actions">
            <button className="btn btn-secondary" onClick={updateBatch} disabled={saving}>
              <Save size={18} />
              {saving ? 'Saving...' : 'Update Details'}
            </button>
          </div>
        )}
      </div>

      {/* Payments Card - Only show after batch is created */}
      {batchId && (
        <div className="card">
          <div className="card-header">
            <h2>Payments ({savedPaymentsCount} saved)</h2>
            <button className="btn btn-secondary" onClick={addPaymentRow}>
              <Plus size={18} />
              Add Payment
            </button>
          </div>

          {payments.length === 0 ? (
            <div className="empty-payments">
              <p>No payments added yet</p>
              <button className="btn btn-primary" onClick={addPaymentRow}>
                <Plus size={18} />
                Add First Payment
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="payments-table">
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th>Cost Centre</th>
                    <th>Amount</th>
                    <th>Reference</th>
                    <th>Invoice #</th>
                    <th>Invoice</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, index) => (
                    <tr key={payment.id || payment.tempId}>
                      <td>
                        <Select
                          value={getSupplierOption(payment.supplier)}
                          onChange={(selected) => updatePayment(index, 'supplier', selected ? selected.value : '')}
                          options={supplierOptions}
                          placeholder="Search supplier..."
                          isClearable
                          isSearchable
                          isDisabled={payment.saved}
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                        />
                      </td>
                      <td>
                        <Select
                          value={getCostCentreOption(payment.cost_centre)}
                          onChange={(selected) => updatePayment(index, 'cost_centre', selected ? selected.value : '')}
                          options={costCentreOptions}
                          placeholder="Select..."
                          isClearable
                          isSearchable
                          isDisabled={payment.saved}
                          styles={{
                            ...selectStyles,
                            control: (base, state) => ({
                              ...selectStyles.control(base, state),
                              minWidth: '180px',
                            }),
                          }}
                          menuPortalTarget={document.body}
                        />
                      </td>
                      <td>
                        <div className="amount-input-group">
                          <input
                            type="number"
                            step="0.01"
                            value={payment.amount}
                            onChange={(e) => updatePayment(index, 'amount', e.target.value)}
                            placeholder="0.00"
                            disabled={payment.saved}
                            className="amount-input"
                          />
                          <span className="currency-suffix">{payment.currency || 'ZAR'}</span>
                        </div>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={payment.reference}
                          onChange={(e) => updatePayment(index, 'reference', e.target.value)}
                          placeholder="INV-001"
                          disabled={payment.saved}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={payment.invoice_number}
                          onChange={(e) => updatePayment(index, 'invoice_number', e.target.value)}
                          placeholder="Optional"
                          disabled={payment.saved}
                        />
                      </td>
                      <td className="invoice-cell">
                        {payment.saved ? (
                          payment.invoiceFileName && payment.invoiceUrl ? (
                            <div className="invoice-actions">
                              <a 
                                href={payment.invoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="invoice-link"
                                title="View invoice"
                                onClick={(e) => {
                                  if (!payment.invoiceUrl) {
                                    e.preventDefault();
                                    alert('Invoice URL not available');
                                  }
                                }}
                              >
                                <FileText size={14} />
                                {payment.invoiceFileName.length > 15 
                                  ? payment.invoiceFileName.substring(0, 12) + '...' 
                                  : payment.invoiceFileName}
                              </a>
                            </div>
                          ) : (
                            <span className="no-invoice">No file</span>
                          )
                        ) : (
                          <div className="invoice-upload-wrapper">
                            {payment.invoiceFileName ? (
                              <div className="invoice-selected">
                                <span className="invoice-name" title={payment.invoiceFileName}>
                                  <FileText size={14} />
                                  {payment.invoiceFileName.length > 10 
                                    ? payment.invoiceFileName.substring(0, 8) + '...' 
                                    : payment.invoiceFileName}
                                </span>
                                <button
                                  type="button"
                                  className="invoice-remove-btn"
                                  onClick={() => handleInvoiceChange(index, null)}
                                  title="Remove invoice"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <label className="invoice-upload-btn">
                                <Upload size={14} />
                                Upload
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleInvoiceChange(index, e.target.files[0])}
                                  style={{ display: 'none' }}
                                />
                              </label>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {payment.saving ? (
                          <span className="status-saving">Saving...</span>
                        ) : payment.saved ? (
                          <span className="status-saved">✓ Saved</span>
                        ) : (
                          <span className="status-unsaved">Unsaved</span>
                        )}
                      </td>
                      <td className="actions-cell">
                        {!payment.saved && (
                          <button
                            className="btn-icon save"
                            onClick={() => savePayment(index)}
                            title="Save payment"
                          >
                            <Save size={16} />
                          </button>
                        )}
                        {payment.saved && (
                          <button
                            className="btn-icon edit"
                            onClick={() => editPayment(index)}
                            title="Edit payment"
                          >
                            <Edit3 size={16} />
                          </button>
                        )}
                        <button
                          className="btn-icon delete"
                          onClick={() => deletePayment(index)}
                          title="Delete payment"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {payments.length > 0 && (
            <div className="payments-summary">
              {Object.keys(currencyTotals).length === 0 ? (
                <span>Total: <strong>0.00</strong></span>
              ) : (
                Object.entries(currencyTotals).map(([currency, total]) => (
                  <span key={currency} style={{ marginRight: '16px' }}>
                    <span className="breakdown-currency-tag">{currency}</span>{' '}
                    <strong>{total.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</strong>
                  </span>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Submit Button - Only show after batch is created */}
      {batchId && (
        <div className="batch-actions">
          <button
            className="btn btn-primary"
            onClick={submitBatch}
            disabled={loading || savedPaymentsCount === 0}
          >
            <Send size={18} />
            {loading ? 'Submitting...' : 'Submit for Approval'}
          </button>
        </div>
      )}
    </div>
  );
};

export default BatchCreate;